import { inviteTokenFromPath } from '@jbroll/rowboat-sharing-react';
import { useRef, useState } from 'react';
import { useActiveEstate } from '@/hooks/useActiveEstate';
import { useReadOnly } from '@/hooks/useReadOnly';
import {
  ANON_IDENTITY,
  RowboatProvider,
  useAuthor,
  usePort,
  useRowboat,
  useSelect,
} from '@/rowboat';
import { listItemGroups } from '@/services/itemGroupService';
import { assignNumber, resolveNumberConflicts } from '@/services/itemNumbers';
import { archiveItem, createItem, type ItemBinding, lastBinding } from '@/services/itemService';
import { AppShell } from './components/AppShell';
import { AuthGate } from './components/AuthGate';
import { FirstRunSetup } from './components/FirstRunSetup';
import { InviteAccept, OpenAcceptedProject } from './components/InviteAccept';
import { ItemDetail } from './components/ItemDetail';
import { ItemList, NO_FILTERS } from './components/ItemList';
import { ProjectMenu } from './components/ProjectMenu';
import { StoreErrorBoundary } from './components/StoreErrorBoundary';
import { UpdateBanner } from './components/UpdateBanner';

function Workspace({
  openItem,
  onOpen,
  onBack,
  onMenu,
}: {
  openItem: string | null;
  onOpen: (id: string) => void;
  onBack: () => void;
  onMenu: () => void;
}) {
  const graph = useRowboat();
  const { author } = usePort();
  const actor = author ?? ANON_IDENTITY;
  const { estates, activeEstate } = useActiveEstate();
  const readOnly = useReadOnly(activeEstate?.owner_group_id);
  // A group or tag filter from another project matches nothing here, and signing in swaps the store.
  const [filters, setFilters] = useState(NO_FILTERS);
  const filterScope = `${activeEstate?.id ?? ''}:${author ?? ''}`;
  const [lastFilterScope, setLastFilterScope] = useState(filterScope);
  if (filterScope !== lastFilterScope) {
    setLastFilterScope(filterScope);
    setFilters(NO_FILTERS);
  }
  // Leaving a blank item archives it, so returning to it (or another device archiving it)
  // lands on the list, as does a row that disappears after it was shown. A missing row that
  // was never shown stays open: a just-created one may not read back yet.
  const openRow = useSelect(() => {
    if (!openItem) return 'none';
    const row = graph.item(openItem)?.$data;
    if (!row) return 'missing';
    return row.archived ? 'archived' : 'live';
  });
  const shown = useRef<string | null>(null);
  if (openRow === 'live') shown.current = openItem;
  const gone = openRow === 'archived' || (openRow === 'missing' && shown.current === openItem);
  // The ref stops two taps in one tick; `adding` disables the Add buttons after that.
  const addInFlight = useRef(false);
  const [adding, setAdding] = useState(false);

  if (estates.length === 0) return <FirstRunSetup />;

  // A new item takes the group of the one entered before it, and its number, so a run of
  // captures in one place needs no picking. The category is left for photo identification to fill.
  // Numbering can wait seconds for the create to read back, so one add runs at a time.
  const addItem = async (from?: ItemBinding) => {
    if (!activeEstate || addInFlight.current) return;
    addInFlight.current = true;
    setAdding(true);
    try {
      const binding = from ?? lastBinding(graph, activeEstate.id);
      const id = await createItem(graph, {
        estateId: activeEstate.id,
        itemGroupId: binding?.itemGroupId ?? '',
        categoryId: '',
        name: '',
        author: actor,
        groupId: activeEstate.owner_group_id,
      });
      const prefix = listItemGroups(graph, activeEstate.id).find(
        (r) => r.id === binding?.itemGroupId,
      )?.prefix;
      if (prefix) {
        try {
          await assignNumber(graph, id, prefix);
        } catch (err) {
          try {
            await archiveItem(graph, id);
          } catch (archiveErr) {
            throw new AggregateError(
              [err, archiveErr],
              'numbering failed, then the cleanup failed',
            );
          }
          throw err;
        }
      }
      onOpen(id);
    } finally {
      addInFlight.current = false;
      setAdding(false);
    }
  };

  if (openItem && !gone) {
    return (
      <ItemDetail
        key={openItem}
        itemId={openItem}
        readOnly={readOnly}
        adding={adding}
        onBack={onBack}
        onAdd={addItem}
        onMenu={onMenu}
      />
    );
  }
  return (
    <ItemList
      estateId={activeEstate?.id ?? ''}
      filters={filters}
      onFilters={setFilters}
      readOnly={readOnly}
      onOpen={onOpen}
      adding={adding}
      onAdd={addItem}
    />
  );
}

export default function App() {
  const [openItem, setOpenItem] = useState<string | null>(null);
  // Signing in adopts the anonymous store, open item included. Any other identity change
  // swaps in a graph without that item, so the open item is dropped before it renders.
  const author = useAuthor();
  const [lastAuthor, setLastAuthor] = useState(author);
  if (author !== lastAuthor) {
    setLastAuthor(author);
    if (lastAuthor !== null) setOpenItem(null);
  }
  const [menuOpen, setMenuOpen] = useState(false);
  // No router: the invite page is the only path besides `/`.
  const [path, setPath] = useState(() => window.location.pathname);
  const [acceptedGroup, setAcceptedGroup] = useState<string | null>(null);
  const navigate = (to: string) => {
    window.history.replaceState(null, '', to);
    setPath(to);
    setMenuOpen(false);
  };
  const inviteToken = inviteTokenFromPath(path);

  return (
    <>
      {/* Only a production build emits /sw.js. */}
      {import.meta.env.PROD && <UpdateBanner />}
      <StoreErrorBoundary>
        <RowboatProvider afterSync={resolveNumberConflicts}>
          {inviteToken ? (
            <InviteAccept
              key={inviteToken}
              token={inviteToken}
              onAccepted={(groupId) => {
                setAcceptedGroup(groupId);
                navigate('/');
              }}
              onLeave={() => navigate('/')}
            />
          ) : (
            <AuthGate
              onInvite={(token) => navigate(`/invite/${token}`)}
              onMenu={() => setMenuOpen(true)}
            >
              {acceptedGroup && (
                <OpenAcceptedProject
                  targetGroupId={acceptedGroup}
                  onDone={() => setAcceptedGroup(null)}
                />
              )}
              {menuOpen && (
                <ProjectMenu
                  onClose={() => setMenuOpen(false)}
                  onSwitch={() => {
                    setMenuOpen(false);
                    setOpenItem(null);
                  }}
                />
              )}
              <AppShell>
                <Workspace
                  openItem={openItem}
                  onOpen={setOpenItem}
                  onBack={() => setOpenItem(null)}
                  onMenu={() => setMenuOpen(true)}
                />
              </AppShell>
            </AuthGate>
          )}
        </RowboatProvider>
      </StoreErrorBoundary>
    </>
  );
}
